import { t } from "@lingui/macro";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import { AlertCircle, CheckCircle, Info, ArrowRight, Edit3, Zap } from "lucide-react";
import { useState } from "react";

type LanguageClarityQualityProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const LanguageClarityQuality = ({
  analysesData,
  className,
}: LanguageClarityQualityProps) => {
  const { isDarkMode } = useTheme();
  const [showAllIssues, setShowAllIssues] = useState(false);

  // Return null if data doesn't exist
  if (!analysesData.metrics.lcq || !analysesData.metrics.lcq.object) {
    return null;
  }

  const { problematic_sentences, bullet_points, summary, action_verb_summary } =
    analysesData.metrics.lcq.object;

  // Get overall score from the weighted score
  const score = analysesData.metrics.lcq.weightedScore / analysesData.metrics.lcq.weight;
  const languageScore = Math.round(score * 100);

  // Get action verb percentage for display
  const actionVerbPercentage = action_verb_summary?.action_verb_percentage || 0;

  // Define colors based on theme
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    amber: "#ffb74d",
    red: isDarkMode ? "#ef5350" : "#e57373",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on score - unified with metric-score-card.tsx
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (languageScore >= 80) status = "excellent";
  else if (languageScore >= 60) status = "good";

  // Get status information - unified with metric-score-card.tsx
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`This aspect of your resume is working very well for this job.`,
          action: t`Keep doing what you're doing - this is a strength!`,
          color: colors.green,
          bgColor: colors.greenBg,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`This area is decent but could be improved for better results.`,
          action: t`Review the suggestions below to make this even stronger.`,
          color: colors.blue,
          bgColor: colors.blueBg,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`This area needs significant improvement to match the job requirements.`,
          action: t`Focus on the recommendations below to improve your chances.`,
          color: colors.red,
          bgColor: colors.redBg,
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Get examples of good action verb usage
  const goodActionVerbs = bullet_points?.filter((bp) => bp.has_action_verb).slice(0, 3) || [];

  // Show first 3 issues by default, all issues when expanded (maintaining order)
  const totalIssuesCount = problematic_sentences.length;
  const issuesToShow = showAllIssues ? problematic_sentences : problematic_sentences.slice(0, 3);

  // Only show "show more" button if there are more than 3 issues total
  const hasMoreToShow = !showAllIssues && totalIssuesCount > 3;

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.color}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Info className="h-5 w-5" style={{ color: statusInfo.color }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.color }}>
              {statusInfo.text} ({languageScore}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.color }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          {summary && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
              <h5 className="mb-1 font-medium text-slate-700 dark:text-slate-300">{t`Language Issues`}</h5>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {summary.total_issues === 0
                  ? t`No issues found`
                  : `${summary.total_issues} ${t`issues to fix`}`}
              </p>
            </div>
          )}
          {action_verb_summary && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
              <h5 className="mb-1 font-medium text-slate-700 dark:text-slate-300">{t`Action Verbs`}</h5>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {actionVerbPercentage}% {t`of bullets use strong verbs`}
              </p>
            </div>
          )}
        </div>

        {/* Issues to Fix */}
        {totalIssuesCount > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Edit3 className="h-5 w-5 text-amber-600" />
                {t`Sentences to Fix`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These sentences have grammar or clarity issues that could make your resume look unprofessional. Fix them to improve your chances.`}
              </p>
            </div>
            <div className="space-y-3">
              {issuesToShow.map((issue, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50"
                >
                  <div className="flex items-start gap-x-3">
                    <div className="flex-1">
                      <div className="mb-2">
                        <p className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">{t`ORIGINAL SENTENCE:`}</p>
                        <p className="rounded border-l-2 border-red-300 bg-red-50 p-2 text-sm font-medium dark:border-red-700 dark:bg-red-900/20">
                          "{issue.sentence}"
                        </p>
                      </div>

                      <div className="mb-2">
                        <p className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">{t`WHAT'S WRONG:`}</p>
                        <div className="rounded-md border-l-2 border-amber-300 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-900/20">
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            {issue.explanation}
                          </p>
                        </div>
                      </div>

                      {issue.suggestion && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">{t`HOW TO FIX IT:`}</p>
                          <div className="rounded-md border-l-2 border-green-300 bg-green-50 p-2 dark:border-green-700 dark:bg-green-900/20">
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">
                              {issue.suggestion}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {(hasMoreToShow || showAllIssues) && (
                <button
                  onClick={() => setShowAllIssues(!showAllIssues)}
                  className="text-sm font-medium text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
                >
                  {showAllIssues ? t`Show less` : t`Show all ${totalIssuesCount} issues`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Good Examples (if user has some good action verbs) */}
        {goodActionVerbs.length > 0 && actionVerbPercentage >= 30 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Zap className="h-5 w-5 text-green-600" />
                {t`Your Strong Bullet Points`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These bullet points start with strong action verbs. Use this style for all your bullets to create more impact.`}
              </p>
            </div>
            <div className="space-y-2">
              {goodActionVerbs.map((bullet, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20"
                >
                  <p className="text-sm text-green-800 dark:text-green-200">{bullet.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
